function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/*
 * Small UI primitives shared by the Audiences list-management tabs
 * (mockups 5 to 8 of bulk-email-2026-10-02.html). Styles: sass/v2/_audiences.scss.
 *
 * Vocabulary (APP_UI_STANDARD + the Email section's form classes, 2026-10-04):
 *   Modal        in-page dialog; the body is an `.ec-form` so `.ec-field` / `.ec-input` / `.ec-check` / `.ec-help` apply
 *   InlineConfirm the "Yes, remove / Keep" pattern (never window.confirm)
 *   Menu         a button that opens a small action list ("Add…")
 *   Chip         clickable filter chip (NOT a status: statuses use the kit's Pill)
 *   GrowthBars   added / removed by week mini chart
 */
import React from "react";
import Chart from "../../components/chart";
import { Button } from "@progress/kendo-react-buttons";
import { Pill } from "../../components/analytics-kit";

/** In-page modal (no browser dialogs). width in px. footer = buttons (Cancel plain + one primary); Escape and the backdrop also close. */
export function Modal({
  title,
  onClose,
  children,
  width = 640,
  busy = false,
  footer,
  note
}) {
  React.useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape" && !busy) onClose && onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);
  return /*#__PURE__*/React.createElement("div", {
    className: "aud-overlay",
    onClick: e => {
      if (e.target === e.currentTarget && !busy && onClose) onClose();
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "aud-modal",
    style: {
      width
    },
    role: "dialog",
    "aria-label": typeof title === "string" ? title : undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: "aud-modal-head"
  }, /*#__PURE__*/React.createElement("div", {
    className: "aud-modal-title"
  }, title)), /*#__PURE__*/React.createElement("div", {
    className: "aud-modal-body ec-form"
  }, children), footer ? /*#__PURE__*/React.createElement("div", {
    className: "aud-modal-foot"
  }, note ? /*#__PURE__*/React.createElement("span", {
    className: "ec-help aud-modal-note"
  }, note) : null, /*#__PURE__*/React.createElement("span", {
    className: "right"
  }), footer) : null));
}

/**
 * Inline destructive confirm: <InlineConfirm question="Remove 3 people?" yes="Yes, remove" onYes onNo busy />
 * variant "row" renders .territory-toggle chip-buttons (inside table rows); default renders Kendo buttons.
 */
export function InlineConfirm({
  question,
  yes = "Yes, remove",
  no = "Keep",
  onYes,
  onNo,
  busy = false,
  variant = "bar"
}) {
  if (variant === "row") {
    return /*#__PURE__*/React.createElement("span", {
      className: "aud-inline-confirm"
    }, question ? /*#__PURE__*/React.createElement("span", null, question) : null, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle danger",
      disabled: busy,
      onClick: onYes
    }, busy ? "…" : yes), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      disabled: busy,
      onClick: onNo
    }, no));
  }
  return /*#__PURE__*/React.createElement("span", {
    className: "aud-inline-confirm"
  }, question ? /*#__PURE__*/React.createElement("span", null, question) : null, /*#__PURE__*/React.createElement(Button, {
    themeColor: "primary",
    disabled: busy,
    onClick: onYes
  }, busy ? "…" : yes), /*#__PURE__*/React.createElement(Button, {
    disabled: busy,
    onClick: onNo
  }, no));
}

/** Action menu: <Menu label="Add…" items={[{label, onClick, hint?, disabled?}]} primary /> */
export function Menu({
  label,
  items,
  primary = false,
  disabled = false
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return /*#__PURE__*/React.createElement("span", {
    className: "aud-menu",
    ref: ref
  }, /*#__PURE__*/React.createElement(Button, {
    themeColor: primary ? "primary" : undefined,
    disabled: disabled,
    onClick: () => setOpen(!open)
  }, label), open ? /*#__PURE__*/React.createElement("div", {
    className: "aud-menu-pop",
    role: "menu"
  }, items.filter(Boolean).map(it => /*#__PURE__*/React.createElement("button", {
    key: it.label,
    type: "button",
    role: "menuitem",
    disabled: it.disabled,
    onClick: () => {
      setOpen(false);
      it.onClick && it.onClick();
    }
  }, /*#__PURE__*/React.createElement("span", {
    className: "l"
  }, it.label), it.hint ? /*#__PURE__*/React.createElement("span", {
    className: "h"
  }, it.hint) : null))) : null);
}

/** Dismissable notice line (success tone by default). */
export function Banner({
  children,
  onClose,
  tone = "ok",
  action
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `aud-banner ${tone}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "t"
  }, children), action ? /*#__PURE__*/React.createElement("a", {
    href: "#",
    className: "aud-banner-action",
    onClick: e => {
      e.preventDefault();
      action.onClick();
    }
  }, action.label) : null, onClose ? /*#__PURE__*/React.createElement("a", {
    href: "#",
    className: "aud-banner-x",
    onClick: e => {
      e.preventDefault();
      onClose();
    }
  }, "Dismiss") : null);
}

/** Clickable filter chip: tone = ok | warn | bad | info | neutral; `on` = selected. */
export function Chip({
  tone = "neutral",
  children,
  title,
  onClick,
  on
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: `aud-mchip ${tone}${onClick ? " clickable" : ""}${on ? " on" : ""}`,
    title: title,
    onClick: onClick
  }, children);
}

/** Reason / status tone (ok|warn|bad|neutral) → kit Pill tone. */
export const pillTone = t => ({
  ok: "success",
  warn: "warning",
  bad: "danger",
  info: "info"
})[t] || "neutral";

/** Consent pill: "opted_in" | "opted_out" | "none" (or the legacy emailOptIn boolean). */
export function ConsentPill({
  value
}) {
  const v = value === true ? "opted_in" : value === false ? "opted_out" : value || "none";
  if (v === "opted_in") return /*#__PURE__*/React.createElement(Pill, {
    tone: "success"
  }, "Opted in");
  if (v === "opted_out") return /*#__PURE__*/React.createElement(Pill, {
    tone: "danger"
  }, "Opted out");
  return /*#__PURE__*/React.createElement(Pill, {
    tone: "neutral"
  }, "No consent");
}

/** Mockup check line: m = ok | warn | bad. */
export function Check({
  m = "ok",
  children
}) {
  const glyph = m === "ok" ? "✓" : m === "bad" ? "✕" : "!";
  return /*#__PURE__*/React.createElement("div", {
    className: "aud-check"
  }, /*#__PURE__*/React.createElement("span", {
    className: `m ${m}`
  }, glyph), /*#__PURE__*/React.createElement("span", null, children));
}

/** Funnel row: label + right-aligned value; cls = minus | total. */
export function FunnelRow({
  label,
  value,
  cls = ""
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `aud-f ${cls}`
  }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", {
    className: "v"
  }, value));
}

/** Key/value line inside a person card. */
export function KV({
  k,
  v
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "aud-kv"
  }, /*#__PURE__*/React.createElement("span", null, k), /*#__PURE__*/React.createElement("span", null, v == null || v === "" ? "—" : v));
}

/** Inline error banner (.ec-err tone, block). */
export function ErrorLine({
  children
}) {
  return children ? /*#__PURE__*/React.createElement("div", {
    className: "aud-error"
  }, children) : null;
}

/** Added / removed by week. weeks = [{week, added, removed}] */
export function GrowthBars({
  weeks,
  height = 72
}) {
  const w = (weeks || []).map(x => ({
    week: String(x.week || x.weekStart || "").slice(0, 10),
    added: Number(x.added) || 0,
    removed: Number(x.removed) || 0
  }));
  if (w.length < 2) return /*#__PURE__*/React.createElement("div", {
    className: "ec-help"
  }, "Weekly adds and removals appear once two weeks of history exist.");
  const label = v => {
    const t = new Date(v + "T00:00:00");
    return isNaN(t) ? v : t.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };
  return /*#__PURE__*/React.createElement(Chart, {
    width: "100%",
    height: `${Math.max(190, height * 2)}px`,
    chartType: "ColumnChart",
    data: [["Week", "Added", "Removed"], ...w.map(x => [label(x.week), x.added, x.removed])],
    options: {
      colors: ["#ff6358", "#9fb3c8"],
      legend: {
        position: "top"
      },
      vAxis: {
        format: "short",
        showTextEvery: 2
      },
      tooltip: {
        format: "{0:n0}"
      }
    }
  });
}

/** Client-side CSV download of the given rows (array of arrays; first row = header). */
export function downloadRowsCsv(rows, fileName) {
  const esc = v => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = rows.map(r => r.map(esc).join(",")).join("\n");
  const blob = new Blob([text], {
    type: "text/csv;charset=utf-8"
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
export const PrimaryButton = props => /*#__PURE__*/React.createElement(Button, _extends({
  themeColor: "primary"
}, props));
export const BaseButton = props => /*#__PURE__*/React.createElement(Button, props);
export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric"
  });
}
export function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit"
  });
  return sameDay ? `today ${time}` : `${fmtDate(v)} ${time}`;
}
export function pct(v, digits = 1) {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${(n <= 1 && n >= 0 && digits > 0 ? n * 100 : n).toFixed(digits)} %`;
}

/** Percent where the API gives a fraction 0..1 OR already a percent; chooses by magnitude. */
export function pctAuto(v, digits = 1) {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${(n <= 1 ? n * 100 : n).toFixed(digits)} %`;
}
export const REASON_LABELS = {
  hard_bounce: "hard bounce",
  soft_bounce_x3: "soft bounce ×3",
  complaint: "complaint",
  unsubscribe: "unsubscribe",
  manual: "manual",
  invalid_syntax: "invalid syntax",
  role_address: "role address",
  disposable: "disposable",
  provider_cleaned: "provider cleaned",
  erased: "erased",
  global_block: "global block"
};
export const REASON_TONES = {
  hard_bounce: "bad",
  complaint: "bad",
  erased: "bad",
  soft_bounce_x3: "warn",
  global_block: "warn"
};
export const RAIL_LABELS = {
  plunk: "Plunk",
  mailchimp: "Mailchimp",
  constant_contact: "Constant Contact",
  smartlead: "Smartlead"
};
export const RULE_LABELS = {
  email_exact: "exact email",
  phone_e164: "phone E.164",
  email_plus_suffix: "email + suffix",
  customer_external: "same customer #",
  name_postal: "name + postal",
  name_phone: "name + phone"
};